import { useState } from 'react';

import { IconCircleCheck, IconEye, IconPrinter } from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import { Plan, PlanPermissions } from '@cfa/pathway-fe-shared/xp';
import { Divider } from '@cfa/react-components';
import { Button } from '@cfa/react-core';

import { useUpdatePlanStatus } from '@/hooks/useUpdatePlanStatus';

import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import PathwayModal from '@/components/popups/PathwayModal';
import { printPlan } from '@/components/print-report';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { plansViewingAtom } from '@/routes/TrainingPlans/ManagePlans/state';
import ArchivedPlanPreviewModal from '@/routes/TrainingPlans/ManagePlans/Tab/Modals/ArchivedPlanPreviewModal';

interface DividerItem {
  type: 'divider';
}

interface ActionMenuItem {
  dataTestId: string;
  icon: React.ReactNode;
  onClick: () => void;
  text: string;
  permission?: keyof PlanPermissions;
  isDestructive?: boolean;
  showWhen?: boolean;
  type?: never;
}

type PlanMenuItem = ActionMenuItem | DividerItem;

interface ArchivedPlanCardMenuProps {
  plan: Plan;
  permissions: Set<keyof PlanPermissions>;
}

const ArchivedPlanCardMenu: React.FC<ArchivedPlanCardMenuProps> = ({
  plan,
  permissions,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();

  const activatePlanMutation = useUpdatePlanStatus(plan, {
    body: {
      status: 'ACTIVE',
    },
    onSuccess: () => {
      track(AmplitudeProductEvent.ACTIVATE_PLAN_CLICKED, {
        plan_id: plan.id,
        plan_type: plan.type,
      });
      setPlansViewing('active');
      setActivatePlanModalOpen(false);
    },
  });

  const [activatePlanModalOpen, setActivatePlanModalOpen] = useState(false);
  const [previewPlanModalOpen, setPreviewPlanModalOpen] = useState(false);

  const setPlansViewing = useSetAtom(plansViewingAtom);

  const allMenuItems: PlanMenuItem[] = [
    {
      dataTestId: 'ThreeDotMenuPreviewPlan',
      icon: <IconEye />,
      onClick: () => setPreviewPlanModalOpen(true),
      text: t('Generic.previewPlan'),
    },
    {
      dataTestId: 'ThreeDotMenuActivePlan',
      icon: <IconCircleCheck />,
      onClick: () => setActivatePlanModalOpen(true),
      text: t('Generic.activatePlan'),
      permission: 'archive',
    },
    { type: 'divider' },
    {
      dataTestId: 'ThreeDotMenuPrintReport',
      icon: <IconPrinter />,
      onClick: () => {
        printPlan(plan);
      },
      text: t('Button.printReport'),
    },
  ];

  const renderPopoverButtons = (items: PlanMenuItem[]): React.ReactNode[] => {
    let prevButtonWasDivider = false;

    return items.reduce<React.ReactNode[]>((acc, curr, index: number) => {
      if ('type' in curr && curr.type === 'divider' && !prevButtonWasDivider) {
        prevButtonWasDivider = true;
        acc.push(
          <Divider
            key={`divider-${index}`}
            style={{ margin: '0.5em 0' }}
            variant="middle"
          />,
        );
      }

      if (!('type' in curr)) {
        const hasPermission =
          !curr.permission || permissions.has(curr.permission);

        const shouldShow = curr.showWhen === undefined || curr.showWhen;

        if (hasPermission && shouldShow) {
          prevButtonWasDivider = false;
          acc.push(
            <PopoverMenuButtonItem
              key={curr.dataTestId}
              dataTestId={curr.dataTestId}
              icon={curr.icon}
              onClick={curr.onClick}
              text={curr.text}
              isDestructive={curr.isDestructive}
            />,
          );
        }
      }
      return acc;
    }, []);
  };

  return (
    <>
      <PopoverMenuButton data-testid="ArchivedPlanCardMenu">
        {renderPopoverButtons(allMenuItems)}
      </PopoverMenuButton>

      <ArchivedPlanPreviewModal
        canActivate={permissions.has('archive')}
        isOpen={previewPlanModalOpen}
        onClose={() => setPreviewPlanModalOpen(false)}
        planId={plan.id}
      />

      <PathwayModal
        isOpen={activatePlanModalOpen}
        onClose={() => setActivatePlanModalOpen(false)}
        className="max-w-[620px]! 2xl:max-w-[620px]!"
        stepKey="activate-plan"
      >
        <PathwayModal.Header>
          <PathwayModal.Title>{t('Generic.activatePlan')}</PathwayModal.Title>
        </PathwayModal.Header>
        <PathwayModal.Body>
          <p className="text-variant-body1">
            {t('TrainingPlans.manageThreeDotMenu.activatePlanModalDescription')}
          </p>
        </PathwayModal.Body>
        <PathwayModal.Footer>
          <div className="flex w-full gap-4 max-sm:flex-col-reverse">
            <Button
              color="secondary"
              variant="outlined"
              fullWidth
              onPress={() => setActivatePlanModalOpen(false)}
              data-testid="PathwayModalSecondaryButton"
            >
              {t('Button.cancel')}
            </Button>
            <Button
              color="secondary"
              variant="filled"
              fullWidth
              isPending={activatePlanMutation.isPending}
              onPress={() => {
                activatePlanMutation.mutate();
              }}
              data-testid="ConfirmationPopUpPrimaryButton"
            >
              {t('Button.activate')}
            </Button>
          </div>
        </PathwayModal.Footer>
      </PathwayModal>
    </>
  );
};

export default ArchivedPlanCardMenu;
